import { AccountUsageIntent } from './enums';
import { AuthenticationResponseJSON, RegistrationResponseJSON } from '@simplewebauthn/server';
import z from 'zod';
import { Account } from './account';
import { Member } from './member';

/**
 * Authentication input types
 */

const mfaMethodType = z.enum(['EMAIL', 'FIDO2']);
export type MfaMethod = z.infer<typeof mfaMethodType>;


export const loginInputSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});
export type LoginInput = z.infer<typeof loginInputSchema>;


export const signupBusinessHubSchema = z.object({
  name: z.string().trim().min(1),
  phone: z.string().trim().min(1).optional(),
  website: z.string().trim().min(1).optional(),
});
export type SignupBusinessHubInput = z.infer<typeof signupBusinessHubSchema>;

export const signupInputSchema = z.object({
  accountName: z.string().trim().min(1),
  email: z.string().email(),
  password: z.string().min(8),
  firstName: z.string().trim().min(1),
  surname: z.string().trim().min(1),
  usageIntent: z.nativeEnum(AccountUsageIntent),
  businessHub: signupBusinessHubSchema.optional(),
}).superRefine((data, ctx) => {
  if (data.usageIntent !== AccountUsageIntent.PERSONAL && !data.businessHub) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['businessHub'],
      message: 'Business hub details are required for work use',
    });
  }
});
export type SignupInput = z.infer<typeof signupInputSchema>;


export const signupCompleteInputSchema = z.object({
  sessionId: z.string().min(1),
});
export type SignupCompleteInput = z.infer<typeof signupCompleteInputSchema>;


export const signupEmailCodeSendSchema = z.object({
  email: z.string().email(),
});
export type SignupEmailCodeSendInput = z.infer<typeof signupEmailCodeSendSchema>;

export const signupEmailCodeVerifySchema = z.object({
  email: z.string().email(),
  code: z.string().trim().regex(/^\d{6}$/, 'Code must be 6 digits'),
});
export type SignupEmailCodeVerifyInput = z.infer<typeof signupEmailCodeVerifySchema>;

export interface SignupEmailCodeSendResult {
  resendAvailableAt: string;
}

export interface SignupEmailCodeVerifyResult {
  verified: true;
}


export const accountNameAvailabilitySchema = z.object({
  accountName: z.string().trim().min(1).max(100),
});
export type AccountNameAvailabilityInput = z.infer<typeof accountNameAvailabilitySchema>;

export interface AccountNameAvailabilityResult {
  available: boolean;
  suggestions: string[];
}


export const refreshTokenSchema = z.object({
  refreshToken: z.string(),
});

export type RefreshTokenInput = z.infer<typeof refreshTokenSchema>;
export const logoutInputSchema = refreshTokenSchema;
export type LogoutInput = z.infer<typeof logoutInputSchema>;

/**
 * Core authentication result types
 */
export interface LoginResult {
  accessToken: string;
  refreshToken: string;
  account: Account;
  /**
   * Members array with at least one member (enforced at type level and runtime)
   * Tuple type [Member, ...Member[]] ensures at least one element
   */
  members: [Member, ...Member[]];
}

export type SignupCheckoutResult =
  | { kind: 'checkout'; url: string }
  | { kind: 'complete'; login: LoginResult };

export interface MfaLoginResult {
  mfaRequired: true;
  mfaMethods: MfaMethod[];
  mfaToken: string;
}

export interface RefreshTokenResult {
  accessToken: string;
  refreshToken: string;
}

export interface LogoutResult {
  message: string;
}

/**
 * MFA input types
 */
export const mfaTokenSchema = z.object({
  mfaToken: z.string(),
});

export const mfaVerifyEmailSchema = z.object({
  mfaToken: z.string(),
  code: z.string(),
});
export type MfaVerifyEmailInput = z.infer<typeof mfaVerifyEmailSchema>;
export type MfaSendEmailCodeInput = z.infer<typeof mfaTokenSchema>;
export type MfaPasskeyChallengeInput = MfaSendEmailCodeInput


export const mfaVerifyPasskeySchema = z.object({
  ...mfaTokenSchema.shape,
  assertion: z.record(z.any()).transform((value) => value as AuthenticationResponseJSON),
});
export type MfaVerifyPasskeyInput = z.infer<typeof mfaVerifyPasskeySchema>;


export const mfaPreferenceSchema = z.object({
  method: mfaMethodType,
});
export type MfaPreferenceInput = z.infer<typeof mfaPreferenceSchema>;

export const mfaToggleSchema = z.object({
  enabled: z.boolean(),
});
export type MfaToggleInput = z.infer<typeof mfaToggleSchema>;


export const passkeyRegisterVerifySchema = z.object({
  deviceDisplayName: z.string().optional(),
  credential: z.record(z.any()).transform((value) => value as RegistrationResponseJSON),
});
export type PasskeyRegisterVerifyInput = z.infer<typeof passkeyRegisterVerifySchema>;

export interface PasskeyRegisterVerifyResult {
  message: string;
}

/**
 * Password reset types
 */
export const passwordResetSendEmailSchema = z.object({
  email: z.string().email(),
});
export type PasswordResetSendEmailInput = z.infer<typeof passwordResetSendEmailSchema>;

export const passwordResetSchema = z.object({
  passwordResetToken: z.string(),
  newPassword: z.string().min(8, { message: 'Password must be at least 8 characters long' }),
});
export type PasswordResetInput = z.infer<typeof passwordResetSchema>;

export interface PasswordResetResult {
  message: string;
}

/**
 * Invitation types
 */
export const inviteCheckSchema = z.object({
  token: z.string(),
});
export type InviteCheckInput = z.infer<typeof inviteCheckSchema>;

export interface InviteCheckResult {
  isVerified: boolean;
  isActive: boolean;
  email: string;
  hubName: string;
  firstName: string;
  surname: string;
}


export const inviteAcceptSchema = z.object({
  token: z.string(),
  password: z.string().min(8).optional(),
});
export type InviteAcceptInput = z.infer<typeof inviteAcceptSchema>;

export interface InviteAcceptResult {
  message: string;
  email: string;
  hubName: string;
  wasNewUser: boolean;
}

/**
 * User info result with MFA method
 */
export interface UserInfoResult {
  account: Account & {
    currentMfaMethod: MfaMethod | null;
  };
  /**
   * Members array with at least one member (enforced at type level and runtime)
   * Tuple type [Member, ...Member[]] ensures at least one element
   */
  members: [Member, ...Member[]];
}
